// 전직 연출(3개월 C10-5): Ev.JobChange(c=1 성공)를 받으면 약 3.2초 컷을 건다. 마을 교관 앞이라 게임은 멈추지 않는다(적 없음).
// 구성: 위·아래 레터박스 + 화면 어둡히기 + 갈래 색 빛기둥과 회전 문양(CSS) + 떠오르는 불씨(작은 캔버스) + 큰 글자(갈래 이름·전직 단계·스킬 두 줄).
// 섬광 규칙(ART-BIBLE 6장, 1초 3회 이하): 타격 번쩍임은 한 번뿐이고 설정 「섬광 줄이기」면 아예 없다. 기둥은 깜빡이지 않고 한 번 솟았다 가라앉는다.
// 탭하면 건너뛴다(처음 0.5초는 눌러도 건너뛰지 않아 확인 버튼 연타가 컷을 지우지 않게 한다). 컷 동안 터치는 이 층이 받아 게임 입력으로 새지 않는다.
// 소리는 audio/decide.ts가 같은 이벤트로, 카메라는 render/index.ts가 같은 이벤트로 낸다(UI는 화면만).
import { Ev, type GameEvent } from '../shared/events';
import type { GameView } from '../shared/types';
import { viewH, viewW } from '../shared/viewport';
import { FONT, el } from './dom';
import { jobCutsceneLines, type JobCutsceneLines } from './jobModel';
import { reducedMotion } from './skillbar';

/** 컷 길이(ms)와 건너뛰기 가능해지는 시간·건너뛰기 페이드 */
export const JOB_CUT_MS = 3200;
export const JOB_CUT_SKIP_AFTER = 500;
const SKIP_FADE_MS = 260;
const STYLE_ID = 'dh-jobcut-style';
const EMBERS = 56;

const CSS =
  '@keyframes dhJcDim{from{opacity:0}to{opacity:1}}' +
  '@keyframes dhJcBarT{from{transform:translateY(-100%)}to{transform:translateY(0)}}' +
  '@keyframes dhJcBarB{from{transform:translateY(100%)}to{transform:translateY(0)}}' +
  '@keyframes dhJcPillar{0%{transform:translateX(-50%) scaleY(0);opacity:0}18%{transform:translateX(-50%) scaleY(1);opacity:1}70%{opacity:.85}100%{transform:translateX(-50%) scaleY(1);opacity:.55}}' +
  '@keyframes dhJcSigil{0%{transform:translate(-50%,-50%) scale(.2) rotate(0);opacity:0}25%{opacity:1}100%{transform:translate(-50%,-50%) scale(1) rotate(160deg);opacity:.9}}' +
  '@keyframes dhJcText{0%{transform:translateY(14px) scale(.9);opacity:0}100%{transform:translateY(0) scale(1);opacity:1}}' +
  '@keyframes dhJcFlash{0%{opacity:0}12%{opacity:.55}100%{opacity:0}}' +
  '.dh-jc{position:absolute;inset:0;z-index:80;overflow:hidden;pointer-events:auto;touch-action:none;cursor:pointer;user-select:none;-webkit-user-select:none;font-family:' + FONT + ';transition:opacity ' + SKIP_FADE_MS + 'ms ease-out}' +
  '.dh-jc.out{opacity:0;pointer-events:none}' +
  '.dh-jc .dim{position:absolute;inset:0;background:radial-gradient(ellipse at 50% 55%,rgba(6,8,18,.72) 0%,rgba(2,3,10,.94) 100%);animation:dhJcDim .4s ease-out both}' +
  '.dh-jc .bar{position:absolute;left:0;right:0;height:13%;background:#02030a;z-index:3}' +
  '.dh-jc .bar.t{top:0;animation:dhJcBarT .35s ease-out both;border-bottom:2px solid var(--jc)}.dh-jc .bar.b{bottom:0;animation:dhJcBarB .35s ease-out both;border-top:2px solid var(--jc)}' +
  '.dh-jc .pillar{position:absolute;left:50%;bottom:0;top:0;width:var(--pw);transform-origin:50% 100%;transform:translateX(-50%) scaleY(0);' +
  'background:linear-gradient(90deg,transparent 0%,color-mix(in srgb,var(--jc) 55%,transparent) 30%,rgba(255,255,255,.6) 50%,color-mix(in srgb,var(--jc) 55%,transparent) 70%,transparent 100%);mix-blend-mode:screen;filter:blur(2px);animation:dhJcPillar 2.9s .15s ease-out both}' +
  '.dh-jc .sigil{position:absolute;left:50%;top:56%;width:var(--sg);height:var(--sg);transform:translate(-50%,-50%) scale(.2);animation:dhJcSigil 2.9s .2s ease-out both;opacity:0}' +
  '.dh-jc canvas{position:absolute;inset:0;width:100%;height:100%;z-index:2;pointer-events:none}' +
  '.dh-jc .flash{position:absolute;inset:0;background:var(--jc);mix-blend-mode:screen;opacity:0;pointer-events:none;animation:dhJcFlash .45s .2s ease-out both;z-index:4}' +
  '.dh-jc .txt{position:absolute;left:0;right:0;top:50%;transform:translateY(-50%);display:flex;flex-direction:column;align-items:center;gap:var(--g1);text-align:center;padding:0 5%;z-index:5}' +
  '.dh-jc .txt>*{opacity:0;animation:dhJcText .5s ease-out both}' +
  '.dh-jc .tt{font:900 var(--f1) ' + FONT + ';color:#fff;letter-spacing:.06em;text-shadow:0 0 18px var(--jc),0 0 40px var(--jc),0 3px 0 rgba(0,0,0,.8);animation-delay:.55s;overflow-wrap:anywhere}' +
  '.dh-jc .sb{font:900 var(--f2) ' + FONT + ';color:#ffe9b8;text-shadow:0 2px 0 rgba(0,0,0,.85),0 0 12px rgba(0,0,0,.8);animation-delay:.8s}' +
  '.dh-jc .sk{display:flex;flex-direction:column;gap:var(--g2);align-items:center;animation-delay:1.05s}' +
  '.dh-jc .sk div{font:800 var(--f3) ' + FONT + ';color:#eef2ff;text-shadow:0 0 6px #000,0 0 12px #000,0 2px 0 rgba(0,0,0,.9);overflow-wrap:anywhere}' +
  '.dh-jc .sk div.l{color:#fff}.dh-jc .sk div.n{color:#9aa6c8}' +
  '.dh-jc .sk b{color:var(--jc);display:inline-block;margin-right:.5em}' +
  '.dh-jc .hint{position:absolute;left:0;right:0;bottom:calc(13% + 6px);text-align:center;font:700 var(--f4) ' + FONT + ';color:rgba(238,242,255,.6);z-index:5;opacity:0;animation:dhJcText .4s 1.6s ease-out both}' +
  '.dh-jc.rm *{animation-duration:.01s!important;animation-delay:0s!important}' +
  '.dh-jc.nf .flash{display:none}';

interface Ember { x: number; y: number; vx: number; vy: number; life: number; max: number; r: number }

export class JobCutscene {
  private readonly root: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly embers: Ember[] = [];
  private cursor: number;
  private startAt = -1;
  private outAt = -1;
  private color = '#ffffff';
  private seed = 1;
  /** 보이는 중인가(QA·테스트가 읽는다) */
  active = false;
  /** 시작한 컷 횟수(QA가 이벤트 도착을 센다) */
  shown = 0;

  constructor(parent: HTMLElement, private readonly view: GameView) {
    ensureStyle();
    this.root = el('div', 'display:none;', parent);
    this.root.className = 'dh-jc'; this.root.dataset.testid = 'job-cutscene'; this.root.setAttribute('role', 'status'); this.root.setAttribute('aria-live', 'polite');
    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d');
    for (let i = 0; i < EMBERS; i++) this.embers.push({ x: 0, y: 0, vx: 0, vy: 0, life: 0, max: 1, r: 1 });
    this.cursor = view.events.written;
    const skip = (e: Event): void => { e.preventDefault(); e.stopPropagation(); this.skip(performance.now()); };
    this.root.addEventListener('pointerdown', skip);
    this.root.addEventListener('click', (e) => e.stopPropagation());
    this.root.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ' || e.key === 'Escape') skip(e); });
  }

  private readonly onEvent = (e: GameEvent): void => {
    if (e.type !== Ev.JobChange || e.c !== 1) return;
    const h = this.view.heroId;
    const lines = jobCutsceneLines(Math.round(e.a), Math.round(e.b), h === 'lyra' ? 'lyra' : 'kael');
    if (lines) this.begin(lines, performance.now());
  };

  /** 건너뛰기(처음 0.5초는 무시) */
  skip(now: number): void {
    if (!this.active || this.outAt >= 0 || now - this.startAt < JOB_CUT_SKIP_AFTER) return;
    this.outAt = now; this.root.classList.add('out');
  }

  /** 컷 시작(QA는 직접 불러 3화면을 캡처한다) */
  begin(l: JobCutsceneLines, now: number): void {
    const rm = reducedMotion(), nf = this.view.settings.reduceFlash;
    const W = viewW(), H = viewH(), k = Math.max(0.6, Math.min(W / 900, H / 420));
    const r = this.root;
    r.textContent = ''; r.className = 'dh-jc' + (rm ? ' rm' : '') + (nf ? ' nf' : '');
    r.style.cssText = 'display:block;--jc:' + l.color + ';--pw:' + Math.round(Math.min(W * 0.34, 260 * k + 40)) + 'px;--sg:' + Math.round(Math.min(W * 0.8, H * 1.05)) + 'px;' +
      '--f1:' + Math.round(40 * k) + 'px;--f2:' + Math.round(20 * k) + 'px;--f3:' + Math.round(17 * k) + 'px;--f4:' + Math.round(12 * k) + 'px;--g1:' + Math.round(8 * k) + 'px;--g2:' + Math.round(4 * k) + 'px;';
    el('div', '', r).className = 'dim';
    el('div', '', r).className = 'pillar';
    const sg = el('div', '', r); sg.className = 'sigil'; sg.innerHTML = sigilSvg(l.color);
    r.appendChild(this.canvas);
    el('div', '', r).className = 'bar t'; el('div', '', r).className = 'bar b';
    el('div', '', r).className = 'flash';
    const t = el('div', '', r); t.className = 'txt';
    el('div', '', t, l.title).className = 'tt';
    el('div', '', t, l.sub).className = 'sb';
    const sk = el('div', '', t); sk.className = 'sk';
    for (const s of l.skills) {
      const d = el('div', '', sk); d.className = s.learned ? 'l' : 'n';
      el('b', '', d, s.learned ? '◆' : '◇');
      d.append(s.name + ' · ' + s.note);
    }
    el('div', '', r, '탭하면 넘어가요').className = 'hint';
    this.color = l.color; this.startAt = now; this.outAt = -1; this.active = true; this.shown++;
    this.canvas.width = Math.max(2, Math.round(W / 2)); this.canvas.height = Math.max(2, Math.round(H / 2));
    for (const e of this.embers) e.life = 0;
    this.seed = 12345 + this.shown;
  }

  private rnd(): number { this.seed = (this.seed * 1664525 + 1013904223) >>> 0; return this.seed / 4294967296; }

  update(u: number, now: number = performance.now()): void {
    this.cursor = this.view.events.readFrom(this.cursor, this.onEvent);
    if (!this.active) return;
    const t = now - this.startAt;
    if ((this.outAt >= 0 && now - this.outAt > SKIP_FADE_MS) || t > JOB_CUT_MS) { this.finish(); return; }
    // 마지막 0.4초는 스스로 사라진다
    if (this.outAt < 0 && t > JOB_CUT_MS - 400) { this.outAt = now - 0; this.root.classList.add('out'); }
    const ctx = this.ctx;
    if (!ctx || reducedMotion()) return;
    const cw = this.canvas.width, ch = this.canvas.height, dt = Math.min(0.05, Math.max(0, u));
    ctx.clearRect(0, 0, cw, ch);
    ctx.globalCompositeOperation = 'lighter';
    const spawn = t > 200 && t < 2400 && this.outAt < 0;
    for (const e of this.embers) {
      if (e.life <= 0) {
        if (!spawn) continue;
        e.x = cw * (0.5 + (this.rnd() - 0.5) * 0.3); e.y = ch * (0.9 + this.rnd() * 0.1);
        e.vx = (this.rnd() - 0.5) * 26; e.vy = -(50 + this.rnd() * 110) * (ch / 200);
        e.max = e.life = 0.9 + this.rnd() * 1.3; e.r = 1 + this.rnd() * 2.2;
      } else {
        e.x += e.vx * dt; e.y += e.vy * dt; e.vy *= 0.992; e.life -= dt;
        if (e.life < 0) e.life = 0;
      }
      if (e.life <= 0) continue;
      const a = Math.min(1, e.life / e.max * 1.6);
      ctx.fillStyle = this.color; ctx.globalAlpha = a * 0.9;
      ctx.beginPath(); ctx.arc(e.x, e.y, e.r, 0, 6.2832); ctx.fill();
      ctx.fillStyle = '#fff'; ctx.globalAlpha = a * 0.7; ctx.fillRect(e.x - e.r * 0.4, e.y - e.r * 0.4, e.r * 0.8, e.r * 0.8);
    }
    ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  }

  private finish(): void {
    this.active = false; this.root.style.display = 'none'; this.root.classList.remove('out');
    this.ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }
}

/** 회전 문양: 이중 고리 + 룬 눈금 + 십자 칼날(갈래 색 선) */
function sigilSvg(c: string): string {
  let ticks = '';
  for (let i = 0; i < 24; i++) { const a = i * 15; ticks += '<line x1="100" y1="6" x2="100" y2="' + (i % 2 ? 14 : 22) + '" transform="rotate(' + a + ' 100 100)"/>'; }
  return '<svg viewBox="0 0 200 200" width="100%" height="100%" aria-hidden="true" fill="none" stroke="' + c + '" stroke-width="1.6" style="filter:drop-shadow(0 0 6px ' + c + ')">' +
    '<circle cx="100" cy="100" r="94"/><circle cx="100" cy="100" r="76" stroke-dasharray="6 5"/><circle cx="100" cy="100" r="52" stroke-width="2.2"/>' + ticks +
    '<path d="M100 40 L108 100 L100 160 L92 100Z M40 100 L100 92 L160 100 L100 108Z" stroke-width="1.8" opacity=".85"/></svg>';
}

function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style'); s.id = STYLE_ID; s.textContent = CSS; document.head.appendChild(s);
}
